import { fromPromise, fromValue, map, mergeMap, pipe, tap } from 'wonka';

import type { Exchange, Operation } from 'urql';

type SelectionSet = Extract<Operation['query']['definitions'][number], { selectionSet: unknown }>['selectionSet'];

// A study's block lists: its task blocks, and an AI-moderated study's contexts.
const BLOCK_LIST_FIELDS = new Set(['task_blocks', 'contexts']);

const selectsBlockList = (selectionSet: SelectionSet): boolean =>
  selectionSet.selections.some(
    selection =>
      (selection.kind === 'Field' && BLOCK_LIST_FIELDS.has(selection.name.value)) ||
      ('selectionSet' in selection && !!selection.selectionSet && selectsBlockList(selection.selectionSet)),
  );

/**
 * The study a mutation answers with a block list for, from the `input._id` every block-list
 * mutation carries; one taking a top-level `_id` instead would not be queued. Fragments ship as
 * definitions in the document, so checking every definition covers them.
 */
const studyAnsweredWithBlockList = (operation: Operation): string | undefined => {
  if (operation.kind !== 'mutation') return undefined;
  const answersWithBlockList = operation.query.definitions.some(
    definition => 'selectionSet' in definition && selectsBlockList(definition.selectionSet),
  );
  if (!answersWithBlockList) return undefined;
  const input: unknown = operation.variables?.input;
  if (typeof input !== 'object' || input === null || !('_id' in input)) return undefined;
  return typeof input._id === 'string' ? input._id : undefined;
};

/**
 * Sends a mutation that answers with a study's block list only once the previous one on that
 * study is answered, so an older list can't arrive last and overwrite a newer one in the cache.
 * A mutation that hangs holds the later ones on its study until it is answered.
 */
export const serialiseMutationsExchange: Exchange = ({ forward }) => {
  // Settles once every mutation queued so far on the study is answered.
  const queueByStudy = new Map<string, Promise<void>>();
  // Identical mutations share an operation key, so each key holds its answers oldest first.
  const pendingAnswers = new Map<number, (() => void)[]>();

  const markAnswered = (key: number) => {
    const [answer, ...waiting] = pendingAnswers.get(key) ?? [];
    answer?.();
    if (waiting.length > 0) pendingAnswers.set(key, waiting);
    else pendingAnswers.delete(key);
  };

  const holdBehindQueue = (operation: Operation, study: string) => {
    const previous = queueByStudy.get(study);
    const answered = new Promise<void>(resolve => {
      pendingAnswers.set(operation.key, [...(pendingAnswers.get(operation.key) ?? []), resolve]);
    });
    const queue = previous ? previous.then(() => answered) : answered;
    queueByStudy.set(study, queue);
    void queue.then(() => {
      if (queueByStudy.get(study) === queue) queueByStudy.delete(study);
    });
    // A promise defers by a microtask, letting a mutation sent after this one overtake it.
    if (!previous) return fromValue(operation);
    return pipe(
      fromPromise(previous),
      map(() => operation),
    );
  };

  return operations$ =>
    pipe(
      forward(
        pipe(
          operations$,
          mergeMap(operation => {
            const study = studyAnsweredWithBlockList(operation);
            return study ? holdBehindQueue(operation, study) : fromValue(operation);
          }),
        ),
      ),
      tap(result => {
        // A streamed answer is complete only on its last part.
        if (result.operation.kind === 'mutation' && !result.hasNext) markAnswered(result.operation.key);
      }),
    );
};
